import { supabase } from "@/integrations/supabase/client";
import {
  BookSummary,
  PaginatedResponse,
  PaginationParams,
  BookView,
  Language,
  SummaryContent,
} from "@/types";

// Function to track a book view using the log_book_view database function
export const trackBookView = async (
  bookId: number,
  userId?: number,
  sessionData?: {
    source?: string;
    app_version?: string;
    platform?: string;
    device_model?: string;
    os_version?: string;
    ip_address?: string;
  }
): Promise<{ isNewView: boolean; message: string }> => {
  try {
    // Call the log_book_view database function
    const { error } = await supabase.rpc('log_book_view', {
      p_user_id: userId || null,
      p_book_id: bookId,
      p_ip: sessionData?.ip_address || "127.0.0.1",
      p_source: sessionData?.source || "web",
      p_app_version: sessionData?.app_version || "1.0.0",
      p_platform: sessionData?.platform || "web",
      p_device_model: sessionData?.device_model || "Desktop",
      p_os_version: sessionData?.os_version || navigator.userAgent,
    });

    if (error) {
      console.error("Error calling log_book_view function:", error);
      throw error;
    }

    console.log(`View tracked for book ID ${bookId} using database function`);
    
    // Since the function uses ON CONFLICT DO NOTHING, we can't easily determine
    // if it was a new view or not without additional logic. For now, we'll
    // return a success message indicating the view was processed.
    return {
      isNewView: true, // We assume it's processed (either new or within window)
      message: "View recorded successfully using 5-minute window deduplication"
    };
  } catch (error) {
    console.error("Error tracking book view:", error);
    throw error;
  }
};

// Function to track a book read
// Event-based book read tracking with UUID session IDs
export const logBookReadEvent = async (
  event: 'start' | 'tick' | 'pause' | 'complete',
  readSessionId: string,
  userId: number,
  bookId: number,
  chapterId: number,
  deltaSeconds: number,
  sessionData?: {
    app_version?: string;
    platform?: string;
    device_model?: string;
    os_version?: string;
  }
): Promise<void> => {
  try {
    const { error } = await supabase.rpc('log_book_read_event', {
      p_event: event,
      p_read_session_id: readSessionId,
      p_user_id: userId,
      p_book_id: bookId,
      p_chapter_id: chapterId,
      p_delta_seconds: deltaSeconds,
      p_app_version: sessionData?.app_version || "1.0.0",
      p_platform: sessionData?.platform || "web",
      p_device_model: sessionData?.device_model || "Desktop",
      p_os_version: sessionData?.os_version || navigator.userAgent,
    });

    if (error) {
      console.error("Error logging book read event:", error);
      throw error;
    }

    console.log(`Book read event '${event}' logged successfully`);
  } catch (error) {
    console.error("Error logging book read event:", error);
    throw error;
  }
};

// Helper function to generate UUID for read session
export const generateReadSessionId = (): string => {
  return crypto.randomUUID();
};

// Simplified test function for backward compatibility
export const trackBookRead = async (
  bookId: number,
  chapterId?: number,
  userId?: number,
  sessionData?: {
    duration_seconds?: number;
    app_version?: string;
    platform?: string;
    device_model?: string;
    os_version?: string;
    ip_address?: string;
  }
): Promise<void> => {
  if (!userId || !chapterId) {
    console.warn("User ID and Chapter ID are required for event-based tracking");
    return;
  }

  try {
    const readSessionId = generateReadSessionId();
    const deltaSeconds = sessionData?.duration_seconds || 600; // Default 10 minutes

    // Log a single complete event with the total time
    // This will create a new session with started_at and ended_at set
    await logBookReadEvent('complete', readSessionId, userId, bookId, chapterId, deltaSeconds, sessionData);

    console.log("Book read session tracked successfully");
  } catch (error) {
    console.error("Error tracking book read:", error);
    throw error;
  }
};

// Function to track an audio play
// Audio event tracking with per-session upsert and real play-time
export const logBookAudioEvent = async (
  event: 'start' | 'tick' | 'pause' | 'complete',
  audioSessionId: string,
  userId: number,
  bookId: number,
  chapterId: number,
  deltaSeconds: number,
  positionSeconds: number,
  playbackSpeed: number,
  sessionData?: {
    app_version?: string;
    platform?: string;
    device_model?: string;
    os_version?: string;
  }
): Promise<void> => {
  try {
    const { error } = await supabase.rpc('log_book_audio_event', {
      p_event: event,
      p_audio_session_id: audioSessionId,
      p_user_id: userId,
      p_book_id: bookId,
      p_chapter_id: chapterId,
      p_delta_seconds: deltaSeconds,
      p_position_seconds: positionSeconds,
      p_playback_speed: playbackSpeed,
      p_app_version: sessionData?.app_version || "1.0.0",
      p_platform: sessionData?.platform || "web",
      p_device_model: sessionData?.device_model || "Desktop",
      p_os_version: sessionData?.os_version || navigator.userAgent,
    });

    if (error) {
      console.error(`Error logging audio ${event} event:`, error);
      throw error;
    }

    console.log(`Audio ${event} event logged for session ${audioSessionId}`);
  } catch (error) {
    console.error(`Error logging audio ${event} event:`, error);
    throw error;
  }
};

// Generate a unique audio session ID
export const generateAudioSessionId = (): string => {
  return crypto.randomUUID();
};

// Simplified function for test integration (backward compatibility)
export const trackBookAudioPlay = async (
  bookId: number,
  chapterId?: number,
  userId?: number,
  sessionData?: {
    duration_seconds?: number;
    playback_position?: number;
    playback_speed?: number;
    was_completed?: boolean;
    app_version?: string;
    platform?: string;
    device_model?: string;
    os_version?: string;
    ip_address?: string;
  }
): Promise<void> => {
  if (!userId) {
    throw new Error("User ID is required for audio tracking");
  }

  const audioSessionId = generateAudioSessionId();
  const deltaSeconds = sessionData?.duration_seconds || 300; // 5 minutes default
  const positionSeconds = sessionData?.playback_position || 180; // 3 minutes position
  const playbackSpeed = sessionData?.playback_speed || 1.5;

  try {
    // Log a complete audio play session
    await logBookAudioEvent(
      'complete',
      audioSessionId,
      userId,
      bookId,
      chapterId || 0,
      deltaSeconds,
      positionSeconds,
      playbackSpeed,
      {
        app_version: sessionData?.app_version,
        platform: sessionData?.platform,
        device_model: sessionData?.device_model,
        os_version: sessionData?.os_version,
      }
    );

    console.log(`Audio play tracked for book ID ${bookId} with session ${audioSessionId}`);
  } catch (error) {
    console.error("Error tracking audio play:", error);
    throw error;
  }
};

// Function to fetch books with pagination
export const fetchBooks = async ({
  page = 1,
  pageSize = 10,
  filters = {},
}: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, string | boolean | number> & {
    sortDirection?: "asc" | "desc";
  };
} = {}): Promise<{
  data: BookSummary[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}> => {
  try {
    // Get the sort direction from filters or default to "desc"
    const sortDirection = filters.sortDirection || "desc";

    const searchTerm = filters.search
      ? String(filters.search).toLowerCase()
      : "";
    const isSearching = searchTerm.length > 0;

    // Detect if we need client-side filtering
    const needsClientSideFiltering =
      (filters.category && filters.category !== "all") ||
      (filters.language && filters.language !== "all") ||
      (filters.access && filters.access !== "all") ||
      (filters.contentType && filters.contentType !== "all") ||
      filters.hasAudio === true;

    // Get all book IDs that match author search, if there's a search term
    let authorMatchingBookIds: number[] = [];
    if (isSearching) {
      try {
        // First check if any authors match the search term
        const { data: authorSearchData, error: authorSearchError } =
          await supabase
            .from("people")
            .select("id, name")
            .ilike("name", `%${searchTerm}%`);

        if (authorSearchError) {
          console.error("Error searching authors:", authorSearchError);
        } else if (authorSearchData && authorSearchData.length > 0) {
          // If we have author matches, get all books linked to these authors
          const authorIds = authorSearchData.map((author) => author.id);

          const { data: bookAuthorData, error: bookAuthorError } =
            await supabase
              .from("books_authors")
              .select("book_id")
              .in("person_id", authorIds);

          if (bookAuthorError) {
            console.error("Error fetching books for authors:", bookAuthorError);
          } else if (bookAuthorData && bookAuthorData.length > 0) {
            authorMatchingBookIds = bookAuthorData.map((item) => item.book_id);
            console.log(
              `Found ${authorMatchingBookIds.length} books matching author search for "${searchTerm}"`
            );
          }
        }
      } catch (err) {
        console.error("Error in author search process:", err);
        // Continue with the query even if author search fails
      }
    }

    // Calculate offset for pagination (might be adjusted for client-side filtering)
    const offset = (page - 1) * pageSize;

    // Start building the query for books
    let query = supabase.from("books").select("*", { count: "exact" });

    // Apply filters if provided
    if (filters) {
      // Search by title or match books with authors that match the search
      if (isSearching) {
        if (authorMatchingBookIds.length > 0) {
          // If we have author matches, search by title OR match by author
          // Using a filter condition that matches either title search OR books by matched authors
          query = query.or(
            `title.ilike.%${searchTerm}%,id.in.(${authorMatchingBookIds.join(
              ","
            )})`
          );
        } else {
          // If no author matches, just search by title
          query = query.ilike("title", `%${searchTerm}%`);
        }
      }

      // Sort by criteria
      if (filters.sortBy) {
        switch (filters.sortBy) {
          case "newest":
            query = query.order("created_at", { ascending: false });
            break;
          case "oldest":
            query = query.order("created_at", { ascending: true });
            break;
          case "updated":
            query = query.order("updated_at", {
              ascending: sortDirection === "asc",
            });
            break;
          case "popularity":
            query = query.order("view_count", { ascending: false });
            break;
          case "rating":
            query = query
              .order("rating", {
                ascending: sortDirection === "asc",
                nullsFirst: false,
              })
              .order("rating_count", { ascending: false }); // Secondary sort by count
            break;
          case "title":
            query = query.order("title", {
              ascending: sortDirection === "asc",
            });
            break;
          case "author":
            // We'll sort by author after fetching the data
            // Since this requires joined data that's not directly sortable in the query
            break;
          case "languages":
            // We'll sort by language count after fetching the data
            // This requires multiple joins, so we'll handle it post-query
            break;
          case "status":
            query = query.order("is_active", {
              ascending: sortDirection === "asc",
            });
            break;
          case "read_count":
            query = query.order("read_count", {
              ascending: sortDirection === "asc",
            });
            break;
          case "audio_play_count":
            query = query.order("audio_play_count", {
              ascending: sortDirection === "asc",
            });
            break;
          default:
            query = query.order("created_at", { ascending: false });
        }
      }
    }

    // Database result interface for raw book data
    interface BookDatabaseResult {
      id: number;
      title: string;
      position?: number;
      cover_image?: string;
      cover_bg_color?: string;
      reading_time?: number;
      created_at: string;
      updated_at: string;
      rating?: number;
      rating_count?: number;
      view_count?: number;
      read_count?: number;
      audio_play_count?: number;
      is_active?: boolean;
      is_free?: boolean;
      isbn?: string;
      publisher?: string;
      publication_year?: number;
      page_count?: number;
    }

    // We'll use different fetching strategies depending on whether we need client-side filtering
    let booksData: BookDatabaseResult[] = [];
    let count = 0;

    if (needsClientSideFiltering) {
      console.log(`Using client-side filtering strategy...`);
      // For client-side filtering, we need to fetch more records to have enough to filter
      // Set a reasonable limit but higher than just one page
      const fetchLimit = Math.min(500, pageSize * 5);

      // Fetch a larger set of data for client-side filtering
      // We'll fetch from the beginning to get the most recent records
      const {
        data: moreBooksData,
        error: moreBooksError,
        count: totalCount,
      } = await query
        .order("created_at", { ascending: false })
        .limit(fetchLimit);

      if (moreBooksError) {
        console.error(
          "Error fetching books for client-side filtering:",
          moreBooksError
        );
        throw moreBooksError;
      }

      booksData = moreBooksData || [];
      count = totalCount || 0;

      console.log(
        `Fetched ${booksData.length} books for client-side filtering`
      );
    } else {
      // Standard pagination for non-filtered queries
      const {
        data: standardBooksData,
        error: standardError,
        count: totalCount,
      } = await query
        .range(offset, offset + pageSize - 1)
        .order("id", { ascending: true });

      if (standardError) {
        console.error(
          "Error fetching books with standard pagination:",
          standardError
        );
        throw standardError;
      }

      booksData = standardBooksData || [];
      count = totalCount || 0;

      console.log(`Fetched ${booksData.length} books with standard pagination`);
    }

    if (!booksData || booksData.length === 0) {
      return {
        data: [],
        total: 0,
        page,
        pageSize,
        totalPages: 0,
      };
    }

    // Get book IDs for related data queries
    const bookIds = booksData.map((book) => book.id);

    // Fetch authors for these books
    const { data: authorsData } = await supabase
      .from("books_authors")
      .select(
        `
        book_id,
        bio,
        people!books_authors_person_id_fkey(
          id,
          name,
          created_at,
          updated_at
        )
      `
      )
      .in("book_id", bookIds);

    // Fetch categories for these books
    const { data: categoriesData } = await supabase
      .from("books_categories")
      .select(
        `
        book_id,
        categories!books_categories_category_id_fkey(
          id, 
          name,
          is_active
        )
      `
      )
      .in("book_id", bookIds);

    // Fetch all summary contents for these books to get translator and narrator IDs
    const { data: allSummaryContentsData } = await supabase
      .from("summary_contents")
      .select("book_id, translator, audio_narrator, content_type")
      .in("book_id", bookIds);

    // Extract unique translator and narrator IDs
    const translatorIds = Array.from(
      new Set(
        allSummaryContentsData
          ?.map((content) => content.translator)
          .filter((id): id is number => id !== null)
      )
    );

    const narratorIds = Array.from(
      new Set(
        allSummaryContentsData
          ?.map((content) => content.audio_narrator)
          .filter((id): id is number => id !== null)
      )
    );

    // Fetch translator details if we have any translator IDs
    const translatorDetails: Record<number, { name: string; id: number }> = {};
    if (translatorIds.length > 0) {
      const { data: translatorsData } = await supabase
        .from("people")
        .select("id, name")
        .in("id", translatorIds);

      if (translatorsData) {
        translatorsData.forEach((translator) => {
          translatorDetails[translator.id] = {
            id: translator.id,
            name: translator.name,
          };
        });
      }
    }

    // Fetch narrator details if we have any narrator IDs
    const narratorDetails: Record<number, { name: string; id: number }> = {};
    if (narratorIds.length > 0) {
      const { data: narratorsData } = await supabase
        .from("people")
        .select("id, name")
        .in("id", narratorIds);

      if (narratorsData) {
        narratorsData.forEach((narrator) => {
          narratorDetails[narrator.id] = {
            id: narrator.id,
            name: narrator.name,
          };
        });
      }
    }

    // Fetch embedding counts per book (batch)
    const embeddingCountByBookId: Record<number, number> = {};
    {
      const { data: embeddingData } = await supabase
        .from("content_embeddings")
        .select("book_id")
        .in("book_id", bookIds);
      if (embeddingData) {
        for (const row of embeddingData) {
          embeddingCountByBookId[row.book_id] = (embeddingCountByBookId[row.book_id] || 0) + 1;
        }
      }
    }

    // Create lookups for faster access
    const authorsByBookId: Record<
      number,
      Array<{
        id: number;
        name: string;
        created_at: string;
        updated_at: string;
        bio?: string | null;
      }>
    > = {};
    const categoriesByBookId: Record<
      number,
      Array<{ id: number; name: string; is_active: boolean }>
    > = {};

    if (authorsData) {
      authorsData.forEach((item) => {
        if (!authorsByBookId[item.book_id]) {
          authorsByBookId[item.book_id] = [];
        }
        if (item.people) {
          authorsByBookId[item.book_id].push({ ...item.people, bio: item.bio });
        }
      });
    }

    if (categoriesData) {
      categoriesData.forEach((item) => {
        if (!categoriesByBookId[item.book_id]) {
          categoriesByBookId[item.book_id] = [];
        }
        if (item.categories) {
          categoriesByBookId[item.book_id].push(item.categories);
        }
      });
    }

    // Transform data to match BookSummary type
    const books: BookSummary[] = await Promise.all(
      booksData.map(async (book) => {
        // Get authors for this book
        const authors = (authorsByBookId[book.id] || []).map((author) => ({
          id: author.id,
          name: author.name,
          roles: [], // We'll simplify and not fetch roles for now
          createdAt: author.created_at,
          updatedAt: author.updated_at,
          bookBio: author.bio || undefined,
        }));

        // Get categories for this book
        const categories = (categoriesByBookId[book.id] || []).map(
          (category) => ({
            id: category.id,
            name: category.name,
            is_active: category.is_active,
          })
        );

        // Fetch summary contents for this book
        const { data: summaryContentsData } = await supabase
          .from("summary_contents")
          .select(
            `
            id,
            book_id,
            language,
            content_type,
            subtitle,
            short_description,
            audio_url,
            audio_title,
            translator,
            audio_narrator,
            audio_duration,
            created_at,
            updated_at
            `
          )
          .eq("book_id", book.id);

        // Create summary contents with chapters
        const summaryContents: SummaryContent[] = [];

        if (summaryContentsData) {
          // Fetch chapters for each summary content
          for (const content of summaryContentsData) {
            const { data: chaptersData } = await supabase
              .from("chapters")
              .select("*")
              .eq("summary_content_id", content.id)
              .order("position", { ascending: true });

            const chapters = chaptersData || [];

            summaryContents.push({
              id: content.id,
              bookId: content.book_id,
              language: content.language,
              contentType: (content.content_type as 'summary' | 'full_book') || 'summary',
              subtitle: content.subtitle,
              shortDescription: content.short_description,
              audioUrl: content.audio_url,
              audioTitle: content.audio_title,
              translator: content.translator
                ? {
                    id: content.translator,
                    name:
                      translatorDetails[content.translator]?.name ||
                      "Unknown Translator",
                    roles: [],
                    createdAt: content.created_at,
                    updatedAt: content.updated_at,
                  }
                : undefined,
              audioNarrator: content.audio_narrator
                ? {
                    id: content.audio_narrator,
                    name:
                      narratorDetails[content.audio_narrator]?.name ||
                      "Unknown Narrator",
                    roles: [],
                    createdAt: content.created_at,
                    updatedAt: content.updated_at,
                  }
                : undefined,
              audioDuration: content.audio_duration,
              chapters: chapters.map((ch) => ({
                id: ch.id,
                title: ch.title,
                label: ch.label,
                content: ch.content,
                startTime: ch.start_time,
                position: ch.position,
                summary_content_id: ch.summary_content_id,
              })),
              createdAt: content.created_at,
              updatedAt: content.updated_at,
            });
          }
        }

        return {
          id: book.id,
          title: book.title,
          authors,
          coverImage: book.cover_image,
          coverBgColor: book.cover_bg_color,
          categories,
          readingTime: book.reading_time,
          createdAt: book.created_at,
          updatedAt: book.updated_at,
          rating: book.rating,
          ratingCount: book.rating_count || 0,
          viewCount: book.view_count,
          read_count: book.read_count || 0,
          audio_play_count: book.audio_play_count || 0,
          isActive: book.is_active,
          isFree: book.is_free,
          summaryContents,
          isbn: book.isbn || undefined,
          publisher: book.publisher || undefined,
          publicationYear: book.publication_year || undefined,
          pageCount: book.page_count || undefined,
          embeddingCount: embeddingCountByBookId[book.id] || 0,
        };
      })
    );

    // Now apply client-side filtering if needed
    let filteredBooks = books;

    console.log(`Original book count: ${filteredBooks.length}`);

    // Apply category filter if needed
    if (filters.category && filters.category !== "all") {
      const categoryId = Number(filters.category);
      console.log(`Filtering by category ID: ${categoryId}`);

      filteredBooks = filteredBooks.filter((book) => {
        const hasCategory = book.categories.some(
          (category) => category.id === categoryId
        );
        return hasCategory;
      });

      console.log(`Books after category filter: ${filteredBooks.length}`);
    }

    // Apply language filter if needed
    if (filters.language && filters.language !== "all") {
      console.log(`Filtering by language: ${filters.language}`);

      filteredBooks = filteredBooks.filter((book) => {
        // Check if the book has any content in the requested language
        const hasLanguage = book.summaryContents.some(
          (content) => content.language === filters.language
        );

        if (hasLanguage) {
          console.log(
            `Book ID ${book.id} "${book.title}" has content in ${filters.language}`
          );
        }

        return hasLanguage;
      });

      console.log(`Books after language filter: ${filteredBooks.length}`);

      // Log the titles of matching books for debugging
      if (filteredBooks.length < 10) {
        console.log("Matching books:");
        filteredBooks.forEach((book, index) => {
          console.log(
            `${index + 1}. ${book.title} (${
              book.summaryContents.length
            } contents)`
          );
        });
      }
    }

    // Apply access filter if needed
    if (filters.access && filters.access !== "all") {
      console.log(`Filtering by access type: ${filters.access}`);

      filteredBooks = filteredBooks.filter((book) => {
        if (filters.access === "free") {
          return book.isFree === true;
        } else if (filters.access === "premium") {
          return book.isFree !== true; // Handle both false and undefined as premium
        }
        return true;
      });

      console.log(`Books after access filter: ${filteredBooks.length}`);
    }

    // Apply audio filter if needed
    if (filters.hasAudio === true) {
      console.log(`Filtering by audio availability`);

      filteredBooks = filteredBooks.filter((book) => {
        const hasAudio = book.summaryContents.some(
          (content) => content.audioUrl && content.audioUrl.trim() !== ""
        );
        return hasAudio;
      });

      console.log(`Books after audio filter: ${filteredBooks.length}`);
    }

    // Apply content type filter if needed
    if (filters.contentType && filters.contentType !== "all") {
      const contentTypeFilter = String(filters.contentType);
      console.log(`Filtering by content type: ${contentTypeFilter}`);

      filteredBooks = filteredBooks.filter((book) => {
        return book.summaryContents.some(
          (content) => content.contentType === contentTypeFilter
        );
      });

      console.log(`Books after content type filter: ${filteredBooks.length}`);
    }

    // Apply sorts that require joined data
    if (filters.sortBy === "author") {
      // Sort by first author's name
      filteredBooks.sort((a, b) => {
        const authorA = a.authors?.[0]?.name || "";
        const authorB = b.authors?.[0]?.name || "";

        return sortDirection === "asc"
          ? authorA.localeCompare(authorB)
          : authorB.localeCompare(authorA);
      });
    } else if (filters.sortBy === "languages") {
      // Sort by number of languages
      filteredBooks.sort((a, b) => {
        return sortDirection === "asc"
          ? a.summaryContents.length - b.summaryContents.length
          : b.summaryContents.length - a.summaryContents.length;
      });
    }

    // Calculate totals for client-side filtering or use server total
    const totalItems = needsClientSideFiltering ? filteredBooks.length : count;

    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    console.log(
      `Total filtered items: ${totalItems}, Total pages: ${totalPages}`
    );

    // Apply pagination to the filtered results
    if (needsClientSideFiltering) {
      const startIndex = (page - 1) * pageSize;
      const endIndex = Math.min(startIndex + pageSize, filteredBooks.length);

      console.log(`Applying pagination: ${startIndex} to ${endIndex}`);

      filteredBooks = filteredBooks.slice(startIndex, endIndex);

      console.log(`Items on page ${page}: ${filteredBooks.length}`);
    }

    return {
      data: filteredBooks,
      total: totalItems,
      page,
      pageSize,
      totalPages,
    };
  } catch (error) {
    console.error("Error in fetchBooks:", error);
    // Return empty result with at least 1 page on error
    return {
      data: [],
      total: 0,
      page,
      pageSize,
      totalPages: 1,
    };
  }
};

// Function to fetch book summaries
export const fetchBookSummaries = async ({
  page = 1,
  pageSize = 10,
  filters = {},
}: {
  page?: number;
  pageSize?: number;
  filters?: Record<string, string | boolean | number>;
} = {}): Promise<{
  data: BookSummary[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}> => {
  return fetchBooks({ page, pageSize, filters });
};

// Function to fetch a specific book by ID
export const fetchBookById = async (
  id: number
): Promise<BookSummary | null> => {
  try {
    console.log("fetchBookById called with id:", id);
    // Fetch the book first
    const { data: bookData, error } = await supabase
      .from("books")
      .select("*")
      .eq("id", id)
      .single();

    if (error || !bookData) {
      console.error("Error fetching book:", error);
      return null;
    }

    console.log("Book data retrieved:", bookData);

    // Fetch authors for this book
    const { data: authorsData } = await supabase
      .from("books_authors")
      .select(
        `
        book_id,
        bio,
        people!books_authors_person_id_fkey(
          id,
          name,
          created_at,
          updated_at
        )
      `
      )
      .eq("book_id", id);

    // Fetch categories for this book
    const { data: categoriesData } = await supabase
      .from("books_categories")
      .select(
        `
        book_id,
        categories!books_categories_category_id_fkey(
          id, 
          name,
          is_active
        )
      `
      )
      .eq("book_id", id);

    // Transform data
    const authors = authorsData
      ? authorsData
          .filter((item) => item.people)
          .map((item) => ({
            id: item.people.id,
            name: item.people.name,
            roles: [], // Simplify - no roles for now
            createdAt: item.people.created_at,
            updatedAt: item.people.updated_at,
            bookBio: item.bio || undefined,
          }))
      : [];

    const categories = categoriesData
      ? categoriesData
          .filter((item) => item.categories)
          .map((item) => ({
            id: item.categories.id,
            name: item.categories.name,
            is_active: item.categories.is_active,
          }))
      : [];

    // Fetch summary contents for this book
    const { data: summaryContentsData, error: summaryContentsError } =
      await supabase
        .from("summary_contents")
        .select(
          `
        id,
        book_id,
        language,
        content_type,
        subtitle,
        short_description,
        audio_url,
        audio_title,
        translator,
        audio_narrator,
        audio_duration,
        ai_context,
        created_at,
        updated_at
      `
        )
        .eq("book_id", bookData.id);

    if (summaryContentsError) {
      console.error("Error fetching summary contents:", summaryContentsError);
    }

    console.log(
      `Retrieved ${
        summaryContentsData?.length || 0
      } summary contents for book ${id}`
    );

    // Fetch all unique narrator IDs
    const narratorIds = Array.from(
      new Set(
        summaryContentsData
          ?.map((content) => content.audio_narrator)
          .filter((id): id is number => id !== null)
      )
    );

    // Fetch all unique translator IDs
    const translatorIds = Array.from(
      new Set(
        summaryContentsData
          ?.map((content) => content.translator)
          .filter((id): id is number => id !== null)
      )
    );

    // Fetch narrator details if we have any narrator IDs
    const narratorDetails: Record<number, { name: string; id: number }> = {};
    if (narratorIds.length > 0) {
      const { data: narratorsData } = await supabase
        .from("people")
        .select("id, name")
        .in("id", narratorIds);

      if (narratorsData) {
        narratorsData.forEach((narrator) => {
          narratorDetails[narrator.id] = {
            id: narrator.id,
            name: narrator.name,
          };
        });
      }
    }

    // Fetch translator details if we have any translator IDs
    const translatorDetails: Record<number, { name: string; id: number }> = {};
    if (translatorIds.length > 0) {
      const { data: translatorsData } = await supabase
        .from("people")
        .select("id, name")
        .in("id", translatorIds);

      if (translatorsData) {
        translatorsData.forEach((translator) => {
          translatorDetails[translator.id] = {
            id: translator.id,
            name: translator.name,
          };
        });
      }
    }

    // Derive languages from summary contents
    const languageSet = new Set<string>();
    const languages: Language[] = [];
    const summaryContents: SummaryContent[] = [];

    if (summaryContentsData) {
      // Fetch chapters for each summary content
      for (const content of summaryContentsData) {
        const { data: chaptersData } = await supabase
          .from("chapters")
          .select("*")
          .eq("summary_content_id", content.id)
          .order("position", { ascending: true });

        console.log(
          `Fetched ${chaptersData?.length || 0} chapters for summary content ${
            content.id
          } (language: ${content.language})`
        );

        const chapters = chaptersData || [];

        // Create the summary content object with chapters
        const summaryContent: SummaryContent = {
          id: content.id,
          bookId: content.book_id,
          language: content.language,
          contentType: (content.content_type as 'summary' | 'full_book') || 'summary',
          subtitle: content.subtitle,
          shortDescription: content.short_description,
          audioUrl: content.audio_url,
          audioTitle: content.audio_title,
          translator: content.translator
            ? {
                id: content.translator,
                name:
                  translatorDetails[content.translator]?.name ||
                  "Unknown Translator",
                roles: [],
                createdAt: content.created_at,
                updatedAt: content.updated_at,
              }
            : undefined,
          audioNarrator: content.audio_narrator
            ? {
                id: content.audio_narrator,
                name:
                  narratorDetails[content.audio_narrator]?.name ||
                  "Unknown Narrator",
                roles: [],
                createdAt: content.created_at,
                updatedAt: content.updated_at,
              }
            : undefined,
          audioDuration: content.audio_duration,
          aiContext: content.ai_context || undefined,
          chapters: chapters.map((ch) => ({
            id: ch.id,
            title: ch.title,
            label: ch.label,
            content: ch.content,
            startTime: ch.start_time,
            position: ch.position,
            summary_content_id: ch.summary_content_id,
          })),
          createdAt: content.created_at,
          updatedAt: content.updated_at,
        };

        console.log(
          `Adding summary content for ${content.language} with ${summaryContent.chapters.length} chapters`
        );
        summaryContents.push(summaryContent);
      }
    }

    // Fetch per-summary-content embedding counts
    const summaryContentIds = summaryContents.map((sc) => sc.id);
    if (summaryContentIds.length > 0) {
      const embeddingCountPromises = summaryContentIds.map(async (scId) => {
        const { count } = await supabase
          .from("content_embeddings")
          .select("id", { count: "exact", head: true })
          .eq("summary_content_id", scId);
        return { scId, count: count || 0 };
      });
      const embeddingCounts = await Promise.all(embeddingCountPromises);
      for (const { scId, count } of embeddingCounts) {
        const sc = summaryContents.find((s) => s.id === scId);
        if (sc) sc.embeddingCount = count;
      }
    }

    const totalEmbeddingCount = summaryContents.reduce(
      (sum, sc) => sum + (sc.embeddingCount || 0),
      0
    );

    const result = {
      id: bookData.id,
      title: bookData.title,
      authors,
      coverImage: bookData.cover_image,
      coverBgColor: bookData.cover_bg_color,
      categories,
      readingTime: bookData.reading_time,
      createdAt: bookData.created_at,
      updatedAt: bookData.updated_at,
      rating: bookData.rating,
      ratingCount: (bookData as { rating_count?: number }).rating_count || 0,
      viewCount: bookData.view_count,
      read_count: (bookData as { read_count?: number }).read_count || 0,
      audio_play_count: (bookData as { audio_play_count?: number }).audio_play_count || 0,
      isActive: bookData.is_active,
      isFree: (bookData as { is_free?: boolean }).is_free,
      summaryContents,
      embeddingCount: totalEmbeddingCount,
      isbn: (bookData as { isbn?: string }).isbn || undefined,
      publisher: (bookData as { publisher?: string }).publisher || undefined,
      publicationYear: (bookData as { publication_year?: number }).publication_year || undefined,
      pageCount: (bookData as { page_count?: number }).page_count || undefined,
    };

    console.log(
      `Returning book with ${summaryContents.length} summary contents`
    );
    return result;
  } catch (error) {
    console.error("Error in fetchBookById:", error);
    return null;
  }
};

// Function to fetch a book summary by ID
export const fetchBookSummary = async (
  id: number
): Promise<BookSummary | null> => {
  try {
    // Log the ID being requested
    console.log("Fetching book summary for ID:", id);

    // Validation
    if (!id || id <= 0) {
      console.error("Invalid book ID:", id);
      return null;
    }

    return await fetchBookById(id);
  } catch (error) {
    console.error("Error in fetchBookSummary:", error);
    return null;
  }
};

// Function to save a single chapter to database
export const saveChapter = async (chapter: {
  id: number;
  title: string;
  label?: string;
  content: string;
  startTime?: number;
  position: number;
  summary_content_id: number;
}): Promise<{ id: number } | null> => {
  try {
    console.log("Saving chapter:", chapter);

    if (!chapter.summary_content_id) {
      console.error("Missing summary_content_id for chapter");
      return null;
    }

    // Prepare the data for insert/update
    const chapterData = {
      title: chapter.title,
      label: chapter.label || "",
      content: chapter.content,
      start_time: chapter.startTime || 0,
      position: chapter.position,
      summary_content_id: chapter.summary_content_id,
    };

    let result;

    // If id is 0, it's a new chapter
    if (chapter.id === 0) {
      // Insert new chapter
      const { data, error } = await supabase
        .from("chapters")
        .insert(chapterData)
        .select("id")
        .single();

      if (error) {
        console.error("Error inserting chapter:", error);
        throw error;
      }

      result = data;
      console.log("Inserted new chapter with ID:", result?.id);
    } else {
      // Update existing chapter
      const { data, error } = await supabase
        .from("chapters")
        .update(chapterData)
        .eq("id", chapter.id)
        .select("id")
        .single();

      if (error) {
        console.error("Error updating chapter:", error);
        throw error;
      }

      result = data;
      console.log("Updated chapter with ID:", result?.id);
    }

    return result;
  } catch (error) {
    console.error("Error in saveChapter:", error);
    return null;
  }
};

// Function to delete a book and all its related records
export const deleteBook = async (bookId: number): Promise<boolean> => {
  try {
    console.log(`Starting deletion process for book ID: ${bookId}`);

    // Step 1: Delete book_views (analytics data)
    console.log(`Deleting view analytics for book ID: ${bookId}`);
    const { error: viewsError } = await supabase
      .from("book_views")
      .delete()
      .eq("book_id", bookId);

    if (viewsError) {
      console.error("Error deleting book views:", viewsError);
      // Continue deletion process even if views deletion fails
    }

    // Step 2: Delete book_reads (reading analytics)
    console.log(`Deleting read analytics for book ID: ${bookId}`);
    try {
      const { error: readsError } = await supabase
        .from("book_reads")
        .delete()
        .eq("book_id", bookId);

      if (readsError && !readsError.message?.includes("does not exist")) {
        console.error("Error deleting book reads:", readsError);
        // Continue deletion process even if reads deletion fails
      }
    } catch (readsError) {
      // If book_reads table doesn't exist, just continue
      console.log("Skipping book_reads deletion:", readsError);
    }

    // Step 3: Delete book_audio_plays (audio analytics)
    console.log(`Deleting audio play analytics for book ID: ${bookId}`);
    try {
      const { error: audioPlaysError } = await supabase
        .from("book_audio_plays")
        .delete()
        .eq("book_id", bookId);

      if (
        audioPlaysError &&
        !audioPlaysError.message?.includes("does not exist")
      ) {
        console.error("Error deleting book audio plays:", audioPlaysError);
        // Continue deletion process even if audio plays deletion fails
      }
    } catch (audioPlaysError) {
      // If book_audio_plays table doesn't exist, just continue
      console.log("Skipping book_audio_plays deletion:", audioPlaysError);
    }

    // Step 4: Find all summary_contents for this book
    const { data: summaryContents, error: fetchError } = await supabase
      .from("summary_contents")
      .select("id")
      .eq("book_id", bookId);

    if (fetchError) {
      console.error("Error fetching summary contents:", fetchError);
      throw fetchError;
    }

    // Step 5: Delete chapters for each summary content
    if (summaryContents && summaryContents.length > 0) {
      const summaryContentIds = summaryContents.map((content) => content.id);
      console.log(
        `Deleting chapters for ${summaryContentIds.length} summary contents`
      );

      const { error: chaptersError } = await supabase
        .from("chapters")
        .delete()
        .in("summary_content_id", summaryContentIds);

      if (chaptersError) {
        console.error("Error deleting chapters:", chaptersError);
        throw chaptersError;
      }
    }

    // Step 6: Delete summary_contents for this book
    console.log(`Deleting summary contents for book ID: ${bookId}`);
    const { error: summaryContentsError } = await supabase
      .from("summary_contents")
      .delete()
      .eq("book_id", bookId);

    if (summaryContentsError) {
      console.error("Error deleting summary contents:", summaryContentsError);
      throw summaryContentsError;
    }

    // Step 7: Delete books_authors relationships
    console.log(`Deleting author relationships for book ID: ${bookId}`);
    const { error: authorsError } = await supabase
      .from("books_authors")
      .delete()
      .eq("book_id", bookId);

    if (authorsError) {
      console.error("Error deleting book authors:", authorsError);
      throw authorsError;
    }

    // Step 8: Delete books_categories relationships
    console.log(`Deleting category relationships for book ID: ${bookId}`);
    const { error: categoriesError } = await supabase
      .from("books_categories")
      .delete()
      .eq("book_id", bookId);

    if (categoriesError) {
      console.error("Error deleting book categories:", categoriesError);
      throw categoriesError;
    }

    // Step 9: Finally delete the book itself
    console.log(`Deleting the book ID: ${bookId}`);
    const { error } = await supabase.from("books").delete().eq("id", bookId);

    if (error) {
      console.error("Error deleting book:", error);
      throw error;
    }

    console.log(
      `Successfully deleted book ID: ${bookId} and all related records`
    );
    return true;
  } catch (error) {
    console.error("Error during book deletion process:", error);
    throw error;
  }
};

// Update book active status
export const updateBookStatus = async (
  id: number,
  isActive: boolean
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("books")
      .update({
        is_active: isActive,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      console.error("Error updating book status:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in updateBookStatus:", error);
    throw error;
  }
};
